import { Send } from "@mui/icons-material";
import {
  Box,
  FormControl,
  IconButton,
  TextField,
  useTheme,
} from "@mui/material";
import React, { KeyboardEvent } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";

import { Form } from "../../../../molecules/form-fields/form/form";
import { Message } from "../../chatbot";

export interface ChatInputProps {
  onMessageSubmit: (message: Message) => void;
}

export const ChatInput: React.FC<ChatInputProps> = ({ onMessageSubmit }) => {
  const theme = useTheme();
  const formMethods = useForm<Message>({
    defaultValues: {
      role: "user",
      content: "",
    },
  });

  const messageContent = useWatch({
    control: formMethods.control,
    name: "content",
  });
  const isMessageEmpty = !messageContent || messageContent.trim().length === 0;

  const handleSubmit = (message: Message) => {
    if (message.content.trim()) {
      onMessageSubmit(message);
      formMethods.reset();
    }
  };

  const handleKeyDown = (evt: KeyboardEvent) => {
    if (evt.key === "Enter" && !evt.shiftKey) {
      evt.preventDefault();
      const message = formMethods.getValues();
      handleSubmit(message);
    }
  };

  return (
    <FormProvider {...formMethods}>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          alignItems: "flex-end",
          gap: 1,
        }}
      >
        <Form
          id="chatbot-message"
          data-testid="ChatbotInput"
          onSubmit={formMethods.handleSubmit(handleSubmit)}
          sx={{
            flex: 1,
            width: "100%",
            display: "flex",
          }}
        >
          <FormControl sx={{ width: "100%", flex: 1 }}>
            <Controller
              name={"content"}
              control={formMethods.control}
              render={({ field: { value, ...field } }) => (
                <TextField
                  autoFocus
                  multiline
                  maxRows={3}
                  value={value}
                  placeholder="Ask a question..."
                  onKeyDown={handleKeyDown}
                  {...field}
                  variant="outlined"
                  size="small"
                  sx={{
                    px: 1,
                    width: "100%",
                  }}
                />
              )}
            />
          </FormControl>
        </Form>
        <IconButton
          color="primary"
          type="submit"
          form="chatbot-message"
          aria-label="send message"
          disabled={isMessageEmpty}
          sx={{
            flexShrink: 0,
            backgroundColor: isMessageEmpty
              ? "rgba(0, 0, 0, 0.12)"
              : theme.palette.primary.main,
            color: isMessageEmpty ? "rgba(0, 0, 0, 0.26)" : "white",
            width: 40,
            height: 40,
            "&:hover": {
              backgroundColor: isMessageEmpty
                ? "rgba(0, 0, 0, 0.12)"
                : theme.palette.primary.main,
            },
            "&:disabled": {
              backgroundColor: "rgba(0, 0, 0, 0.12)",
              color: "rgba(0, 0, 0, 0.26)",
            },
          }}
        >
          <Send sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
    </FormProvider>
  );
};

export default ChatInput;
